import React, { useState, useEffect } from 'react'

const STORAGE_KEY = '__coverage_backup__'

const CoverageCollector = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [stats, setStats] = useState(null)

  // Restore coverage from sessionStorage on mount (survives page refresh)
  useEffect(() => {
    const saved = sessionStorage.getItem(STORAGE_KEY)
    if (saved && window.__coverage__) {
      try {
        const prev = JSON.parse(saved)
        Object.keys(prev).forEach(file => {
          if (window.__coverage__[file]) {
            const cur = window.__coverage__[file]
            const old = prev[file]
            Object.keys(old.s).forEach(key => {
              cur.s[key] += old.s[key]
            })
            Object.keys(old.f).forEach(key => {
              cur.f[key] += old.f[key]
            })
            Object.keys(old.b).forEach(key => {
              old.b[key].forEach((count, i) => {
                cur.b[key][i] += count
              })
            })
          }
        })
      } catch (e) {
        // ignore corrupt data
      }
    }
  }, [])

  // Persist coverage to sessionStorage every 30 seconds
  useEffect(() => {
    if (!window.__coverage__) return
    const interval = setInterval(() => {
      if (window.__coverage__) {
        sessionStorage.setItem(
          STORAGE_KEY,
          JSON.stringify(window.__coverage__)
        )
      }
    }, 30000)
    return () => clearInterval(interval)
  }, [])

  if (!window.__coverage__) return null

  const getCoverageStats = () => {
    const cov = window.__coverage__
    const fileKeys = Object.keys(cov)
    const filesLoaded = fileKeys.length
    let filesCovered = 0
    let totalStatements = 0
    let coveredStatements = 0
    let totalBranches = 0
    let coveredBranches = 0
    let totalFunctions = 0
    let coveredFunctions = 0
    fileKeys.forEach(key => {
      const fileCov = cov[key]
      let fileHasCoverage = false
      Object.values(fileCov.s).forEach(count => {
        totalStatements++
        if (count > 0) {
          coveredStatements++
          fileHasCoverage = true
        }
      })
      Object.values(fileCov.b).forEach(branch => {
        branch.forEach(count => {
          totalBranches++
          if (count > 0) coveredBranches++
        })
      })
      Object.values(fileCov.f).forEach(count => {
        totalFunctions++
        if (count > 0) coveredFunctions++
      })
      if (fileHasCoverage) filesCovered++
    })
    const pct = totalStatements > 0
      ? ((coveredStatements / totalStatements) * 100).toFixed(1)
      : '0.0'
    const branchPct = totalBranches > 0
      ? ((coveredBranches / totalBranches) * 100).toFixed(1)
      : '0.0'
    const fnPct = totalFunctions > 0
      ? ((coveredFunctions / totalFunctions) * 100).toFixed(1)
      : '0.0'
    return {
      filesLoaded,
      filesCovered,
      totalStatements,
      coveredStatements,
      pct,
      branchPct,
      fnPct
    }
  }

  const downloadCoverage = () => {
    const data = JSON.stringify(window.__coverage__)
    const blob = new Blob([data], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'coverage-' + new Date().toISOString().replace(/[:.]/g, '-') + '.json'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const resetCoverage = () => {
    const cov = window.__coverage__
    Object.keys(cov).forEach(file => {
      const fileCov = cov[file]
      Object.keys(fileCov.s).forEach(key => {
        fileCov.s[key] = 0
      })
      Object.keys(fileCov.b).forEach(key => {
        fileCov.b[key] = fileCov.b[key].map(() => 0)
      })
      Object.keys(fileCov.f).forEach(key => {
        fileCov.f[key] = 0
      })
    })
    sessionStorage.removeItem(STORAGE_KEY)
    setStats(getCoverageStats())
  }

  const handleToggle = () => {
    if (!isOpen) {
      setStats(getCoverageStats())
    }
    setIsOpen(!isOpen)
  }

  const refreshStats = () => {
    setStats(getCoverageStats())
  }

  const btnStyle = {
    display: 'block',
    width: '100%',
    padding: '6px',
    marginBottom: '4px',
    background: '#2ecc71',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px'
  }

  return (
    <div style={{
      position: 'fixed',
      bottom: '10px',
      right: '10px',
      zIndex: 999999,
      fontFamily: 'monospace',
      fontSize: '12px'
    }}>
      {!isOpen ? (
        <button
          onClick={handleToggle}
          style={{
            background: '#ff6b35',
            color: 'white',
            border: 'none',
            borderRadius: '50%',
            width: '40px',
            height: '40px',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: 'bold',
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
          }}
          title="QA Coverage Tools"
        >
          C
        </button>
      ) : (
        <div style={{
          background: '#1a1a2e',
          color: '#eee',
          padding: '12px',
          borderRadius: '8px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
          minWidth: '240px'
        }}>
          <div style={{ marginBottom: '8px', fontWeight: 'bold' }}>
            QA Coverage
            <span
              onClick={handleToggle}
              style={{ float: 'right', cursor: 'pointer' }}
            >
              ✕
            </span>
          </div>
          {stats && (
            <div style={{ marginBottom: '8px', lineHeight: '1.6' }}>
              Files loaded: {stats.filesLoaded}<br />
              Files covered: {stats.filesCovered}/{stats.filesLoaded}<br />
              Statements: {stats.coveredStatements}/{stats.totalStatements} ({stats.pct}%)<br />
              Branches: {stats.branchPct}%<br />
              Functions: {stats.fnPct}%
            </div>
          )}
          <button onClick={refreshStats} style={btnStyle}>
            Refresh Stats
          </button>
          <button onClick={downloadCoverage} style={btnStyle}>
            Download Coverage
          </button>
          <button
            onClick={resetCoverage}
            style={{ ...btnStyle, background: '#e74c3c' }}
          >
            Reset Counters
          </button>
        </div>
      )}
    </div>
  )
}

export default CoverageCollector
